import { IconName, type ColumnDef, type IHedgeRule } from '@talos/kyoko';
import type { IRowNode } from 'ag-grid-community';
import { useMemo } from 'react';
import { usePositionAutoHedgingRulesDrawer } from '../../../../components/AutoHedgingRulesDrawer/AutoHedgingRulesDrawer';
import { useRollupTreeRef } from '../../../../hooks';

export const useHedgeRuleControlsColumns = (): ColumnDef<IHedgeRule>[] => {
  const { openPositionAutoHedgingRulesDrawer } = usePositionAutoHedgingRulesDrawer();
  const rollupTreeRef = useRollupTreeRef();
  return useMemo(
    () =>
      [
        {
          field: 'Mode',
          type: 'connectionStatus',
          title: 'Enabled',
          width: 65,
        },
        {
          field: 'AccountID',
          title: 'Monitored Rollup',
          type: 'subAccountName',
          params: {
            rollupTree: rollupTreeRef,
          },
        },
        {
          field: 'Asset',
          title: 'Monitored Asset',
          type: 'currency',
        },
        {
          field: 'TargetAsset',
          type: 'currency',
        },
        {
          field: 'ShortPositionLimit',
          title: 'Short Threshold',
          type: 'number',
          params: { currencyField: 'TargetAsset' },
        },
        {
          field: 'TargetPosition',
          type: 'number',
          params: {
            currencyField: 'TargetAsset',
          },
        },
        {
          field: 'LongPositionLimit',
          title: 'Long Threshold',
          type: 'number',
          params: { currencyField: 'TargetAsset' },
        },
        {
          field: 'TargetAccountID',
          title: 'Hedge Account',
          type: 'subAccount',
        },
        {
          field: 'HedgeStrategy',
          type: 'strategy',
        },
        {
          field: 'HedgeAggregation',
          type: 'pricingAggregation',
        },
        { field: 'HedgeRuleID', type: 'id', hide: true, params: { maxLength: 6 } },
        { field: 'Timestamp', type: 'text', hide: true },
        { field: 'OrgID', type: 'text', hide: true },
        { field: 'UpdateAction', type: 'text', hide: true },
        { field: 'Revision', type: 'text', hide: true },
        { field: 'UpdateUser', type: 'text', hide: true },
        { field: 'Priority', type: 'text', hide: true },
        {
          id: 'edit',
          type: 'iconButton',
          pinned: 'right',
          width: 60,
          suppressColumnsToolPanel: true,
          params: {
            onClick: ({ node }: { node: IRowNode<IHedgeRule> }) => {
              if (!node.data) {
                return;
              }
              openPositionAutoHedgingRulesDrawer({ hedgeRuleID: node.data.HedgeRuleID });
            },
            icon: IconName.Pencil,
          },
        },
      ] satisfies ColumnDef<IHedgeRule>[],
    [openPositionAutoHedgingRulesDrawer, rollupTreeRef]
  );
};
